import { parseCssColor } from "./releaseGlow";

export interface ThemeVariantLike {
  id: string;
  label: string;
  swatch: string;
  accent: string;
  style: Record<string, string>;
}

export interface ThemeBands {
  background: string;
  surface: string;
  accent: string;
  ink: string;
}

const HUE_NAMES: Array<[number, string]> = [
  [12, "Crimson"],
  [38, "Ember"],
  [62, "Gold"],
  [92, "Olive"],
  [148, "Moss"],
  [184, "Teal"],
  [214, "Tide"],
  [248, "Indigo"],
  [288, "Violet"],
  [328, "Orchid"],
  [348, "Rose"],
  [360, "Crimson"],
];

function hueAndSaturation(color: string | undefined): { h: number; s: number } | null {
  const rgb = parseCssColor(color);
  if (!rgb) return null;
  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0 };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return { h: h * 60, s };
}

export function hueName(color: string | undefined): string {
  const tone = hueAndSaturation(color);
  if (!tone) return "Custom";
  if (tone.s < 0.14) return "Stone";
  const hue = ((tone.h % 360) + 360) % 360;
  return HUE_NAMES.find(([limit]) => hue < limit)?.[1] ?? "Crimson";
}

export function themeBands(variant: ThemeVariantLike): ThemeBands {
  const style = variant.style ?? {};
  return {
    background: style["--backgroundColor"] || variant.swatch,
    surface: style["--color5"] || style["--backgroundColor2"] || variant.swatch,
    accent: style["--logoColor2"] || variant.accent,
    ink: style["--logoColor"] || style["--textColor"] || variant.accent,
  };
}

export function themeVariantNames(variants: readonly ThemeVariantLike[]): string[] {
  const used = new Map<string, number>();
  return variants.map((variant) => {
    const base = variant.id === "primary" ? "Original" : hueName(variant.style?.["--accentColor"] || variant.swatch);
    const count = (used.get(base) ?? 0) + 1;
    used.set(base, count);
    return count === 1 ? base : `${base} ${count}`;
  });
}

export function nextRadioIndex(current: number, key: string, count: number): number | null {
  if (count <= 0) return null;
  if (key === "ArrowRight" || key === "ArrowDown") return (current + 1) % count;
  if (key === "ArrowLeft" || key === "ArrowUp") return (current - 1 + count) % count;
  if (key === "Home") return 0;
  if (key === "End") return count - 1;
  return null;
}
